import { HttpArcgis } from '../../http';
import { LocalStorage } from '../../localStorage';
import { getDataEcp, highlightBc, prepareCodeToShow } from './ecpHelper';
import {
  cellReturnData,
  cellReturnDetailIcon,
  cellReturnZoomIcon,
  columnComponent,
  dataStatColumnsHydro,
  doColumns,
  ecpColumns,
  indusColumns,
  milieuColumns,
  pluieColumns,
  PopupComponent,
  prColumns,
  h2sColumns,
  adminUsersColumns,
  saveDataInStorage,
} from '../table/tableHelper';
import IconComponent from '../../../components/common/component/IconComponent';
import HydroDetail from '../../../Views/HydroDetail';
import React from 'react';
import {
  h2sSublayers,
  prepareBCRequest,
  stringifyColorsBis,
} from '../../helper';
import {
  updateDefExpForBlackPointLayer,
} from './hydroHelper';
import DetailIcon from '../../../assets/Table/showDetails.svg';
import PencilIcon from '../../../assets/Table/edit.svg';
import PluieDetail from '../../../Views/PluieDetail';
import { lockForConsultProfiles } from '../../../components/authorization/authorizationHelper';
import ParamComponent from '../../../Views/component/ParamComponent';
import { DilutionDetail } from '../../../Views/DilutionDetail';
import ECPDetail from '../../../Views/EcpDetail/ECPDetail';
import EcpStepInstallParamPopup from '../../../Views/EcpDetail/EcpStepInstallParamPopup';
import H2sDetail from '../../../Views/H2sDetail/H2sDetail';
import { getModuleType, layerIdsByScale } from '../map/mapHelper';
import rfdc from 'rfdc';
import { createRoot } from 'react-dom/client';
import PopupUser from '../../../Views/administration/userView/PopupUser';
import { app_placeholder } from '../../../env.placeholders';
import { EcrouIcon } from '../../../components/common/Icon/EcrouIcon';
import { getEcpMode, isStepMode } from '../view/ecpDetailHelper';

const cloneDeep = rfdc();

export const showRightLayer = (type: string, layer: any, option?: string) => {
  // IDs des sublayers à toujours garder visibles
  let alwaysVisible = new Set([5, 3, 110, 210]);
  
  // For ECP STEP mode, hide ALL measurement and reference layers except base map
  if (type === 'ecp') {
    if (isStepMode()) {
      // Keep border/reference contour layers visible in STEP mode.
      alwaysVisible = new Set([5, 3]);
    }
  }

  // Mapping type → IDs à afficher
  let typeToIds: Record<string, number[] | (() => number[])> = {
    do:    [4, 101, 201, 106, 206, 103, 203],
    pr:    [102, 202, 205, 105, 103, 203],
    h2s:   [...h2sSublayers(), 103, 203],
    ecp:   [107, 207, 103, 203],
    indus: [80, 103, 203],
    hydro: [301, 302, 306, 103, 203],
    pluie: [111, 211, 103, 203],
  };

  // For ECP module, check if STEP mode is active and switch to STEP layer (203)
  if (type === 'ecp') {
    if (isStepMode()) {
      typeToIds.ecp = [103, 203]; // Use STEP min/max layers in STEP mode (Station d'épuration)
    }
  }

  // Masquer tous les sublayers sauf ceux à garder
  layer.allSublayers.items.forEach((lay: any) => {
    if (!alwaysVisible.has(lay.id)) {
      lay.visible = false;
    }
  });

  // For STEP mode in ECP, explicitly hide layer 52 and all other module layers
  if (type === 'ecp') {
    if (isStepMode()) {
      // Hide all ECP PR layers
      [52, 107, 207].forEach(id => {
        const layToHide = layer.findSublayerById?.(id);
        if (layToHide) layToHide.visible = false;
      });
      // Hide all other module layers (DO, PR, H2S, INDUS, HYDRO, PLUIE, MILIEU)
      [4, 101, 201, 106, 206, 102, 202, 205, 105, 80, 301, 302, 306, 111, 211, 108, 208, 109, 209].forEach(id => {
        const layToHide = layer.findSublayerById?.(id);
        if (layToHide) layToHide.visible = false;
      });
      // Also hide measurement sublayers and reference layers
      [110, 210].forEach(id => {
        const layToHide = layer.findSublayerById?.(id);
        if (layToHide) layToHide.visible = false;
      });
    }
  }

  // Déterminer les IDs à afficher
  let idsToShow: number[] = [];
  if (type in typeToIds) {
    const val = typeToIds[type];
    idsToShow = typeof val === 'function' ? val() : val;
  } else {
    // Cas "milieu"
    let id = 108, idDuplicate = 208;
    if (option !== 'sensibility') {
      id = 109;
      idDuplicate = 209;
    }
    idsToShow = [id, idDuplicate];
  }

  // Afficher les sublayers concernés
  idsToShow.forEach((id) => {
    const sub = layer.findSublayerById?.(id);
    if (sub) sub.visible = true;
  });
};

export const getDataPayload = (pModule: string, pQuery: string, pOption?: string) => {
  // form for request sevice
  const formBody: string[] = [];
  formBody.push('where=' + pQuery);
  formBody.push('returnGeometry=true');
  formBody.push('f=json');
  formBody.push('user=' + LocalStorage.userMail.get());

  let id = '';
  if (pModule === 'do') {
    id = '50';
    formBody.push(
      'outFields=Commune,Description,ModelCode,Color,Code,RecordDate',
    );
  } else if (pModule === 'pr') {
    id = '51';
    formBody.push(
      'outFields=Commune,Description,ModelCode,Color,CriticityCode,Code,RecordDate',
    );
  } else if (pModule === 'h2s') {
    id = '60';
    formBody.push(
      `outFields=${switchIndicatorsAttrs(
        pOption!,
      )},Commune,Description,ModelCode,Code`,
    );
  } else if (pModule === 'ecp') {
    // Both PR and STEP modes use layer 52 for final table data
    // PR: gets codes from FilterApi.getBassin()
    // STEP: gets codes from Layer 108, filtered by ModelCode in WHERE clause
    id = '52';
    formBody.push(
      'outFields=Commune,Description,ModelCode,Color,CollectionPoolCode,Code,RecordDate',
    );
  } else if (pModule === 'pluie') {
    id = '55';
    formBody.push(
      'outFields=Commune,Description,ModelCode,Color,Volume,Mesure,Code,MesureRecordDate',
    );
  } else {
    // milieu
    id = '53';
    formBody.push(
      'outFields=Commune,Description,DilutionNote,DilutionColor,SensibilityNote,SensibilityColor,ModelCode,Code,DilutionRecordDate,SensibilityRecordDate',
    );
  }
  const formBodyToSend = formBody.join('&');
  return {
    id: id,
    formBodyToSend: formBodyToSend,
  }
};

// method to feed table for do, pr, milieu (sensibility and dilution) and pluie modules
export const getData = async (
  pModule: string,
  pQuery: string,
  pOption?: string,
) => {
  // prepare queries
  let data: any[] = [];
  const getPayload = getDataPayload(pModule, pQuery, pOption);
  let datas: any[] = [];

  try {
    // launch request
    const responseSections = await HttpArcgis.post(
      app_placeholder.VITE_APP_ARCGIS_SERVER + '?path=/' + getPayload.id + '/query',
      JSON.stringify(getPayload.formBodyToSend),
    );

    if (
      responseSections.status === 200 &&
      responseSections.data.features !== undefined
    ) {
      datas = responseSections.data.features;
      datas.forEach((feat) => {
        // get geometry for zoom
        feat['attributes'].geometry = feat['geometry'];
        data.push(feat['attributes']);
      });
    } else {
      console.log('Aucune données !');
    }
  } catch (error) {
    console.log(
      'Failed to get data for table of ' + pModule + ' feature !',
      error,
    );
  }
  return data;
};

const stepPassiveModules = new Set(['pr', 'do', 'h2s', 'ecp', 'indus', 'hydro', 'pluie']);

const extractInClause = (query: string, fieldName: string) => {
  const matcher = new RegExp(`${fieldName}\\s+in\\s*\\(([^)]+)\\)`, 'i');
  const match = query.match(matcher);
  return match ? `${fieldName} in (${match[1]})` : '';
};

const buildStepPassiveDefinitionExpression = (
  query: string,
  moduleName: string,
) => {
  if (!query || query.trim() === '' || query.trim() === '1=0') {
    return '1=0';
  }

  const clauses: string[] = [];
  const contractClause = extractInClause(query, 'ContractCode');
  const sessionContractCode = window.sessionStorage.getItem('contractCode');
  const inseeClause = extractInClause(query, 'InseeCode');

  // ECP STEP map visibility must stay broad enough to render symbols reliably.
  // Restrictive installation-level clauses can hide all STEP symbols on 103/203.
  if (moduleName === 'ecp' && isStepMode()) {
    const ecpStepClauses: string[] = [];
    if (contractClause) {
      ecpStepClauses.push(contractClause);
    }
    if (inseeClause) {
      ecpStepClauses.push(inseeClause);
    }
    ecpStepClauses.push("ModelCode in ('MDI_SE')");
    return ecpStepClauses.join(' AND ');
  }

  if (contractClause) {
    clauses.push(contractClause);
  } else if (sessionContractCode) {
    // INDUS map filters are often OBJECTID-based and may not include ContractCode.
    // Keep STEP passive layers bounded to the selected contract to avoid out-of-scope symbols.
    clauses.push("ContractCode in ('" + sessionContractCode + "')");
  }
  if (inseeClause) {
    clauses.push(inseeClause);
  }
  clauses.push("ModelCode in ('MDI_SE')");

  return clauses.join(' AND ');
};

export const showHideLayers = (
  pNewDefExp: string,
  pModule: string,
  pLayers: any,
  pBcRenderer: any,
  pSpatialFilter: any,
  pOption: string,
) => {
  // var for getting layers
  let asset, assetDuplicate, assetTP, assetDuplicateTP, assetBC;
  const moduleName: string = getModuleType(pModule, window.sessionStorage.getItem('location')!);
  const layersConfig = layerIdsByScale.find(
    (module) => module.module === moduleName,
  );

  // active layers on map
  showRightLayer(pModule, pLayers, pOption);
  if (!layersConfig) {
    return;
  }
  // get layers to update definition expression
  asset = pLayers.findSublayerById(layersConfig.lowScale?.[0]);
  assetDuplicate = pLayers.findSublayerById(layersConfig.bigScale?.[0]);

  if (pModule !== 'pluie') {
    // assets for pluvio layer
    assetTP =
      layersConfig.lowScale?.[1] !== undefined
        ? pLayers.findSublayerById(layersConfig.lowScale?.[1])
        : null;
    assetDuplicateTP =
      layersConfig.bigScale?.[1] !== undefined
        ? pLayers.findSublayerById(layersConfig.bigScale?.[1])
        : null;
  }

  if (pModule === 'hydro') {
    // specific case
    let assetBlackPoint = pLayers.findSublayerById(306);
    // update def exp
    if (assetBlackPoint) {
      assetBlackPoint.definitionExpression = updateDefExpForBlackPointLayer(
        pNewDefExp,
        pSpatialFilter,
      );
    }
  }

  // apply new def for layers on map
  if (asset && assetDuplicate) {
    asset.definitionExpression = pNewDefExp;
    window.sessionStorage.setItem(
      pModule + 'DefinitionExpression',
      asset.definitionExpression,
    );
    assetDuplicate.definitionExpression = pNewDefExp;
  }
  if (assetTP && assetDuplicateTP) {
    // special case : actually no categ for MR data, so we don't get Color filter for this map api
    if (pModule === 'pr' && assetTP.id === 105 && assetDuplicateTP.id === 205) {
      const spliter = pNewDefExp.split(' AND ');
      const u = spliter.filter((f) => f.includes('Color') === false);
      const united = u.join(u.length === 1 ? '' : ' AND ');
      assetTP.definitionExpression = united;
      assetDuplicateTP.definitionExpression = united;
    } else {
      assetTP.definitionExpression = pNewDefExp;
      assetDuplicateTP.definitionExpression = pNewDefExp;
    }
    window.sessionStorage.setItem(
      pModule + 'DefinitionExpression',
      assetTP.definitionExpression,
    );
  }

  if (stepPassiveModules.has(pModule)) {
    const stepLowScaleLayer = pLayers.findSublayerById(103);
    const stepBigScaleLayer = pLayers.findSublayerById(203);
    const stepDefExp = buildStepPassiveDefinitionExpression(pNewDefExp, pModule);

    if (stepLowScaleLayer) {
      stepLowScaleLayer.definitionExpression = stepDefExp;
    }
    if (stepBigScaleLayer) {
      stepBigScaleLayer.definitionExpression = stepDefExp;
    }
  }

  // highlight (skip in STEP mode)
  if (pModule === 'ecp') {
    if (!isStepMode()) {
      // PR mode: apply collection pool highlighting
      assetBC = pLayers.findSublayerById(3);
      if (assetBC) {
        // highlight or not
        if (JSON.parse(window.sessionStorage.getItem('bassinecp')!)?.name !== undefined && JSON.parse(window.sessionStorage.getItem('bassinecp')!)?.name !== '') {
          assetBC.renderer = highlightBc(
            JSON.parse(window.sessionStorage.getItem('bassinecp')!)?.name,
          );
        } else if (pBcRenderer?.type) {
          assetBC.renderer = pBcRenderer;
        }
      }
    }
  }
};

// renders of table hence module
export const renderTableData = (
  module: string,
  history: any,
  pDataDetail: any[],
  zoomToPointTrigger: (point: {x: number, y: number}) => void,
  pOption?: string,
  pRoles?: string,
) => {
  let columns: any[] = [];
  let idStart: number;
  switch (module) {
    case 'do':
      columns = cloneDeep(doColumns);
      // add buttons
      columns[4] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 0,
          enableSorting: false,
          id: 'do-zoom',
        },
        cellReturnZoomIcon(zoomToPointTrigger!),
      );
      columns[5] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 0,
          enableSorting: false,
          id: 'do-detail',
        },
        cellReturnDetailIcon(history, module),
      );
      return columns;
    case 'pr':
      columns = cloneDeep(prColumns);
      columns[4] = columnComponent(
        'Criticité',
        {
          size: 80,
          accessorKey: 'CriticityCode',
          muiTableHeadCellProps: {
            align: 'center',
          },
        },
        cellReturnData('_pr-criticite', 'CriticityCode'),
      );
      // apply rights
      const showPrMrParam = lockForConsultProfiles(pRoles!);
      // manage adding columns
      idStart = showPrMrParam === false ? 4 : 5;
      if (showPrMrParam === true) {
        columns[5] = columnComponent(
          'none',
          {
            minWidth: 35,
            size: 10,
            enableSorting: false,
            id: 'pr-param',
          },
          (row: any) => {
            // MR installations do not have PR parameters, hide the gear icon for them
            const isMr = row.row.original.Code?.includes('MR');
            if (isMr) {
              return <div id={row.cell.id}></div>;
            }
            const prMrClickEvent = () => {
              saveDataInStorage(row);
              const node = document.createElement('divPrMrParam');
              document.body.appendChild(node);
              const root = createRoot(node!);
              root.render(
                <ParamComponent
                  IsVisible={true}
                  title={row.row.original.Description}
                  mode='prMr'
                />
              );
            };
            return (
              <div id={row.cell.id}>
                <div
                  onClick={prMrClickEvent}
                  onKeyDown={prMrClickEvent}
                  style={{ cursor: 'pointer' }}
                >
                  <EcrouIcon />
                </div>
              </div>
            );
          },
        );
      }
      columns[idStart + 1] = columnComponent(
        'none',
        {
          minWidth: 0,
          size: 0,
          enableSorting: false,
          id: 'pr-zoom',
        },
        cellReturnZoomIcon(zoomToPointTrigger!),
      );
      columns[idStart + 2] = columnComponent(
        'none',
        {
          minWidth: 0,
          size: 0,
          enableSorting: false,
          id: 'pr-detail',
        },
        cellReturnDetailIcon(history, module),
      );
      return columns;
    case 'ecp':
      // ecpColumns() reads ecp_mode from sessionStorage and returns PR or STEP columns
      columns = cloneDeep(ecpColumns());
      const ecpMode = getEcpMode();

      // STEP mode: expose a dedicated STEP installation parameter popup per row.
      if (ecpMode === 'step' && lockForConsultProfiles(pRoles!)) {
        columns[columns.length] = columnComponent(
          'none',
          {
            minWidth: 35,
            size: 10,
            enableSorting: false,
            id: 'ecp-param',
          },
          (row: any) => {
            const ecpStepParamClickEvent = () => {
              saveDataInStorage(row);
              const node = document.createElement('divEcpStepParam');
              document.body.appendChild(node);
              const root = createRoot(node!);
              root.render(
                <PopupComponent
                  tagName='divEcpStepParam'
                  content={<EcpStepInstallParamPopup row={row.row.original} />}
                  style={{
                    position: 'relative',
                    background: '#FFFFFF',
                    borderRadius: '14px',
                    padding: '18px 20px',
                    border: '1px solid #999',
                    width: '680px',
                    maxWidth: '92vw',
                    margin: '8vh auto 0 auto',
                    maxHeight: '78vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                />
              );
            };
            return (
              <div id={row.cell.id}>
                <div
                  className='iconComponent'
                  onClick={ecpStepParamClickEvent}
                  onKeyDown={ecpStepParamClickEvent}
                  style={{ cursor: 'pointer' }}
                >
                  <EcrouIcon />
                </div>
              </div>
            );
          },
        );
      }

      // Action columns always appended after all data columns (no hardcoded indices)
      columns[columns.length] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 0,
          enableSorting: false,
          id: 'ecp-zoom',
        },
        cellReturnZoomIcon(zoomToPointTrigger!),
      );
      columns[columns.length] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 0,
          enableSorting: false,
          id: 'ecp-detail',
        },
        (row: any) => {
          return (
            <div id={row.cell.id}>
              <IconComponent
                icon={DetailIcon}
                event={() => {
                  // create node for popup
                  const node = document.createElement('divEcpDetail');
                  document.body.appendChild(node);
                  const root = createRoot(node!);
                  // add to render
                  root.render(
                    <PopupComponent
                      tagName='divEcpDetail'
                      title={row.row.original.Description}
                      content={
                        <ECPDetail IsVisible={true} row={row.row.original} />
                      }
                      style={{
                        position: 'relative',
                        background: '#F5F8FB',
                        borderRadius: '14px',
                        padding: '20px',
                        border: '1px solid #999',
                        height: '100vh',
                      }}
                    />
                  );
                }}
                imgStyles={{ cursor: 'pointer' }}
              />
            </div>
          );
        },
      );
      return columns;
    case 'h2s':
      columns = cloneDeep(h2sColumns());
      // apply rights
      const showH2SParam = lockForConsultProfiles(pRoles!);
      // manage adding columns
      idStart = showH2SParam === false ? 4 : 5;
      if (showH2SParam === true) {
        columns[4] = columnComponent(
          'none',
          {
            minWidth: 35,
            size: 10,
            enableSorting: false,
            id: 'h2s-param',
          },
          (row: any) => {
            const h2sClickEvent = () => {
              saveDataInStorage(row);
              const node = document.createElement('divH2sParam');
              document.body.appendChild(node);
              const root = createRoot(node!);
              root.render(
                <ParamComponent
                  IsVisible={true}
                  title={row.row.original.Description}
                  mode='h2s'
                />
              );
            };
            return (
              <div id={row.cell.id}>
                <div
                  onClick={h2sClickEvent}
                  onKeyDown={h2sClickEvent}
                  style={{ cursor: 'pointer' }}
                >
                  <EcrouIcon />
                </div>
              </div>
            );
          },
        );
      }
      columns[idStart] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 10,
          enableSorting: false,
          id: 'h2s-zoom',
        },
        cellReturnZoomIcon(zoomToPointTrigger!),
      );
      columns[idStart + 1] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 10,
          enableSorting: false,
          id: 'h2s-detail',
        },
        (row: any) => {
          return (
            <div id={row.cell.id}>
              <IconComponent
                icon={DetailIcon}
                event={() => {
                  // create node for popup
                  const node = document.createElement('divH2sDetail');
                  document.body.appendChild(node);
                  const root = createRoot(node!);
                  // add to render
                  root.render(
                    <PopupComponent
                      tagName='divH2sDetail'
                      content={
                        <H2sDetail IsVisible={true} row={row.row.original} />
                      }
                      style={{
                        position: 'relative',
                        background: '#F5F8FB',
                        borderRadius: '14px',
                        padding: '20px',
                        border: '1px solid #999',
                        height: '100vh',
                      }}
                    />
                  );
                }}
                imgStyles={{ cursor: 'pointer' }}
              />
            </div>
          );
        },
      );
      return columns;
    case 'indus':
      columns = cloneDeep(indusColumns);
      columns[5] = columnComponent(
        'none',
        {
          // minWidth: 35,
          size: 20,
          enableSorting: false,
          id: 'indus-zoom',
        },
        cellReturnZoomIcon(zoomToPointTrigger!),
      );
      return columns;
    case 'hydro':
      columns = cloneDeep(dataStatColumnsHydro);
      columns[2] = columnComponent(
        'none',
        {
          minWidth: 20,
          size: 0,
          enableSorting: false,
          id: 'hydro-detail',
        },
        (row: any) => {
          return (
            <div id={row.cell.id}>
              <IconComponent
                icon={DetailIcon}
                event={() => {
                  // create node for popup
                  const node = document.createElement('divHydroDetail');
                  document.body.appendChild(node);
                  const root = createRoot(node!);
                  // add to render
                  root.render(
                    <PopupComponent
                      tagName='divHydroDetail'
                      content={
                        <HydroDetail
                          IsVisible={true}
                          row={row.row.original}
                          data={pDataDetail}
                        />
                      }
                      style={{
                        position: 'relative',
                        width: '70%',
                        margin: '0 auto',
                        height: 'auto',
                        maxHeight: '70vh',
                        marginTop: 'calc(100vh - 85vh - 20px)',
                        background: '#fff',
                        borderRadius: '14px',
                        padding: '10px',
                        border: '1px solid #999',
                        overflow: 'auto',
                      }}
                    />
                  );
                }}
                imgStyles={{ cursor: 'pointer' }}
              />
            </div>
          );
        },
      );
      return columns;
    case 'pluie':
      columns = cloneDeep(pluieColumns);
      columns[6] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 0,
          enableSorting: false,
          id: 'pluie-zoom',
        },
        cellReturnZoomIcon(zoomToPointTrigger!),
      );
      columns[7] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 0,
          enableSorting: false,
          id: 'pluie-detail',
        },
        (row: any) => {
          return (
            <div id={row.cell.id}>
              <IconComponent
                icon={DetailIcon}
                event={() => {
                  // create node for popup
                  const node = document.createElement('divPluieDetail');
                  document.body.appendChild(node);
                  const root = createRoot(node!);
                  // add to render
                  root.render(
                    <PopupComponent
                      tagName='divPluieDetail'
                      content={
                        <PluieDetail
                          IsVisible={true}
                          row={row.row.original}
                        />
                      }
                      style={{
                        position: 'relative',
                        background: '#fff',
                        borderRadius: '14px',
                        padding: '20px',
                        border: '1px solid #999',
                        overflow: 'auto',
                        height: 'inherit',
                      }}
                    />
                  );
                }}
                imgStyles={{ cursor: 'pointer' }}
              />
            </div>
          );
        },
      );
      return columns;
    case 'admin-users':
      columns = cloneDeep(adminUsersColumns);
      columns[3] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 0,
          enableSorting: false,
          id: 'admin-users-modify',
        },
        (row: any) => {
          return (
            <div id={row.cell.id} style={{ height: 20, width: 20, marginLeft: 'auto', marginRight: 'auto' }}>
              <IconComponent
                icon={PencilIcon}
                event={() => {
                  // create node for popup
                  const node = document.createElement('divAdminUsersModify');
                  document.body.appendChild(node);
                  const root = createRoot(node!);
                  // add to render
                  root.render(
                    <PopupComponent
                      tagName='divAdminUsersModify'
                      content={
                        <PopupUser
                          view='modify'
                          row={row.row.original}
                          getUsersUpdated={row.row.original.getUsersUpdated}
                        />
                      }
                      style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        background: '#fff',
                        borderRadius: '14px',
                        padding: '20px',
                        border: '1px solid #999',
                        width: '30%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                  );
                }}
                imgStyles={{ cursor: 'pointer' }}
              />
            </div>
          );
        },
      );
      return columns;
    default:
      // milieu
      columns = cloneDeep(milieuColumns);
      // apply rights
      const showMilieuParam = lockForConsultProfiles(pRoles!);
      // manage adding columns
      idStart = showMilieuParam === false ? 4 : 5;
      if (showMilieuParam === true) {
        columns[4] = columnComponent(
          'none',
          {
            minWidth: 35,
            size: 0,
            enableSorting: false,
            id: pOption + '-param',
          },
          (row: any) => {
            const milieuClickEvent = () => {
              saveDataInStorage(row);
              const node = document.createElement('divMilieuParam');
              document.body.appendChild(node);
              const root = createRoot(node!);
              root.render(
                <ParamComponent
                  IsVisible={true}
                  title={row.row.original.Description}
                  mode='milieu'
                />
              );
            };
            return (
              <div id={row.cell.id}>
                <div
                  onClick={milieuClickEvent}
                  onKeyDown={milieuClickEvent}
                  style={{ cursor: 'pointer' }}
                >
                  <EcrouIcon />
                </div>
              </div>
            );
          },
        );
      }
      columns[idStart] = columnComponent(
        'none',
        {
          minWidth: 35,
          size: 0,
          enableSorting: false,
          id: pOption + '-zoom',
        },
        cellReturnZoomIcon(zoomToPointTrigger!),
      );
      if (pOption !== 'sensibility') {
        columns[idStart + 1] = columnComponent(
          'none',
          {
            minWidth: 35,
            size: 10,
            enableSorting: false,
            id: pOption + '-detail',
          },
          (row: any) => {
            return (
              <div id={row.cell.id}>
                <IconComponent
                  icon={DetailIcon}
                  event={() => {
                    // create node for popup
                    const node = document.createElement('divDilutionDetail');
                    document.body.appendChild(node);
                    const root = createRoot(node!);
                    // add to render
                    root.render(
                      <PopupComponent
                        tagName='divDilutionDetail'
                        content={
                          <DilutionDetail
                            IsVisible={true}
                            row={row.row.original}
                          />
                        }
                        style={{
                          position: 'relative',
                          background: '#fff',
                          borderRadius: '14px',
                          padding: '20px',
                          border: '1px solid #999',
                          overflow: 'auto',
                          height: 'inherit',
                          width: '50%',
                          right: '-25%',
                        }}
                      />
                    );
                  }}
                  imgStyles={{ cursor: 'pointer' }}
                />
              </div>
            );
          },
        );
      }
      return columns;
  }
};

// use to show Detail view
export const showDetails = (rowInfo, history, module) => {
  if (module === 'do' || module === 'pr') {
    if (module === 'do') {
      LocalStorage.doName.set(rowInfo.row.original.Description);
    } else {
      LocalStorage.prName.set(rowInfo.row.original.Description);
      LocalStorage.prCode.set(rowInfo.row.original.Code);
    }

    window.sessionStorage.setItem('Code', rowInfo.row.original.Code);
    // prevContractCode used for changing contract on PrDetail and DoDetail pages and backword on PrTable page
    const currentContractCode = window.sessionStorage.getItem('contractCode')!;

    if (currentContractCode !== null) {
      window.sessionStorage.setItem('prevContractCode', currentContractCode);
    }
  }

  if (module === 'pr') {
    history('/PRDetaille');
  } else if (module === 'do') {
    history('/DODetaille');
  } else if (module === 'ecp') {
    window.sessionStorage.setItem('PoolCode', rowInfo.row.original.OBJECTID);
    history('/ECPDetaille');
  } else if (module === 'h2s') {
    LocalStorage.prCode.set(rowInfo.row.original.Code);
    window.sessionStorage.setItem('Code', rowInfo.row.original.Code);
    LocalStorage.prName.set(rowInfo.row.original.Description);
    history('/H2SDetaille');
  } else if (module === 'collectionPool') {
    window.sessionStorage.setItem(
      'collectionPoolName',
      rowInfo.row.original.collectionPoolName,
    );
    window.sessionStorage.setItem(
      'collectionPoolId',
      rowInfo.row.original.collectionPoolId,
    );

    history('/CollectionPoolDetaille');
  }
};

export const colorFormated = (colorFilter: object) => {
  let codeColors = stringifyColorsBis(colorFilter).join();
  return { code: codeColors };
};

export const getField = (pModule: string) => {
  let field: string = '';
  switch (pModule) {
    case 'sensibility':
      field = 'SensibilityRecordDate';
      break;
    case 'dilution':
      field = 'DilutionRecordDate';
      break;
    case 'pluie':
      field = 'MesureRecordDate';
      break;
    default:
      field = 'RecordDate';
  }
  return field;
};

export const defineDataDate = (pData: any[], pField: string) => {
  const msDate = pData.find((l) => l[pField] !== null);
  if (msDate !== undefined) {
    let date = new Date(msDate[pField]);
    date.setDate(date.getDate() + 20);
    if (date.getDate() > 15) {
      date.setMonth(date.getMonth() + 1);
    }
    return date.getMonth() + '/' + date.getFullYear();
  } else {
    return '';
  }
};

export const isEmptyLayer = (pInseeCode: any | string) => {
  let res = true;
  if (pInseeCode === '1=0') {
    res = false;
  }
  if (pInseeCode !== null && pInseeCode.data && pInseeCode.data.inter === '') {
    res = false;
  }
  return res;
};

export const updateData = async (
  pAsset: any,
  getHydroData: Function,
  prevFilters: Object,
) => {
  // update table
  await getHydroData(
    JSON.parse(window.sessionStorage.getItem('bcAndCommuneHydro')!),
    pAsset,
    'extentUpdated',
    prevFilters,
  );
};

// trigger to update table and map with filters
export const handleCallback = async (
  pFilters: object,
  prevFilters: Object,
  updataTableAndMap: Function,
) => {
  if (JSON.stringify(pFilters) !== JSON.stringify(prevFilters)) {
    console.log('update table and map');
    updataTableAndMap(pFilters);
  }
};

// name of categ field is different for milieu module
export const getFieldNameForColors = (pFilters: object) => {
  let fieldName: string = '';
  if ('SensibilityColor' in pFilters) {
    fieldName = 'SensibilityColor';
  } else if ('DilutionColor' in pFilters) {
    fieldName = 'DilutionColor';
  } else if ('ColorBoonLister' in pFilters) {
    fieldName = 'ColorBoonLister';
  } else if ('ColorSejourCana' in pFilters) {
    fieldName = 'ColorSejourCana';
  } else if ('ColorSejourPR' in pFilters) {
    fieldName = 'ColorSejourPR';
  } else if ('ColorAutocurage' in pFilters) {
    fieldName = 'ColorAutocurage';
  } else if ('ColorTerrain' in pFilters) {
    fieldName = 'ColorTerrain';
  } else if ('YearFromCurrentYear' in pFilters) {
    fieldName = 'YearFromCurrentYear';
  } else {
    fieldName = 'Color';
  }
  return fieldName;
};

export const resetAvalPointHighlight = () => {
  window.sessionStorage.setItem('zoomAvalPoint', JSON.stringify(null));
  window.sessionStorage.setItem('codesEcp', '');  
};

export const createInputs = (
  pResponse: any,
  pAvalPointCode: string,
  pQuery: string,
  pAllBc: any[],
) => {
  let ecpQuery = '';
  let mode = '';
  const mapCodes: string[] = Array.isArray(pResponse?.mapCodes)
    ? pResponse.mapCodes
    : [];
  if (pResponse.filter !== '') {
    // update table
    let codes: string | undefined = '';
    let avalPoint;
    if (pResponse.dataTable.length !== 0 || mapCodes.length !== 0) {
      // if need highlight
      let getAvalPointGeometry: any = [];
      let getAvalPointCodes: string = '';
      if (pAvalPointCode !== '') {
        // collection pool selected
        getAvalPointGeometry = pResponse.dataTable.filter(
          (d) => d.Code === pAvalPointCode,
        )[0];
        if (getAvalPointGeometry === undefined && mapCodes.includes(pAvalPointCode)) {
          getAvalPointCodes = pAvalPointCode;
        }
      } else {
        if (mapCodes.length !== 0) {
          getAvalPointCodes = mapCodes.join("','");
        } else {
          let installs: string[] = [];
          installs = pResponse.dataTable.filter(
            (d) => d.Code !== null && d.Code !== '',
          );
          getAvalPointCodes =
            installs.length !== 0
              ? installs.map((d: any) => d.Code).join("','")
              : '';
        }
      }
      if (pAvalPointCode !== '' && getAvalPointGeometry !== undefined) {
        avalPoint = {
          geom: getAvalPointGeometry.geometry,
          code: getAvalPointGeometry.Code,
        };
        ecpQuery = pQuery;
        // memorize params to zoom and add event to trigger zoom method in ArcgisMap script
        window.sessionStorage.setItem(
          'zoomAvalPoint',
          JSON.stringify(avalPoint),
        );
        // get codes to show
        codes = prepareCodeToShow(pAllBc, pAvalPointCode);
        // memorize for refreshing page
        window.sessionStorage.setItem('codesEcp', codes);
      } else if (getAvalPointCodes !== '') {
        ecpQuery = pQuery + " AND Code in ('" + getAvalPointCodes + "')";
        resetAvalPointHighlight();
      } else {
        ecpQuery = '1=0';
      }
    } else {
      ecpQuery = '1=0';
      resetAvalPointHighlight();
    }
  } else {
    // empty table
    ecpQuery = '1=0';
    resetAvalPointHighlight();
    // no data on map
    mode = '1=0';
  }
  return {
    ecpQuery: ecpQuery,
    mode: mode,
  };
};

export const ecpGetDataTable = async (
  pQuery: string,
  pParam: any,
  pAllBc: any,
  pSessionContractCode: string | null,
  pFilters: any,
) => {
  let avalPointCode = pParam;
  const response = await getDataEcp(
    pAllBc,
    avalPointCode,
    pSessionContractCode,
    pFilters,
  );
  const inputs = createInputs(response, avalPointCode, pQuery, pAllBc);
  return {
    query: inputs.ecpQuery,
    mode: inputs.mode,
    data: response.dataTable,
  };
};

export const indusCreateInputQuery = (pQuery: string, pResponse: any) => {
  //update map
  let objectIds, inputQuery;
  if (pResponse.length !== 0) {
    // update layers
    objectIds = prepareBCRequest(pResponse);
    // get filters
    inputQuery = 'OBJECTID in (' + objectIds + ')';
    if (pQuery !== '') {
      inputQuery += ' AND ' + pQuery;
    }
  } else {
    // no objectids found, so no data to show
    inputQuery = '1=0';
  }
  return inputQuery;
};

export const filtersFormated = (pFilters: any, pModule: string) => {
  // get color filter or not
  const fieldName = getFieldNameForColors(pFilters);
  let colors: any[] | string =
    pModule !== 'hydro' && pFilters['Color'] !== ''
      ? stringifyColorsBis(pFilters[fieldName]).join()
      : '';
  if (typeof colors !== 'string') {
    colors = '';
  }

  // update or get filters
  const filters =
    pModule !== 'hydro'
      ? {
          ...pFilters,
          [fieldName]: { code: colors },
        }
      : { ...pFilters };

  return {
    fieldName: fieldName,
    filters: filters,
    colors: colors,
  };
};

// Switch H2S endpoints attributes function
export const switchIndicatorsAttrs = (pOption: string) => {
  switch (pOption) {
    case 'IndicatorBoonLister':
      return 'ValueBoonLister,ColorBoonLister,RecordDateBoonLister';
    case 'IndicatorSejourCana':
      return 'ValueSejourCana,ColorSejourCana,RecordDateSejourCana';
    case 'IndicatorSejourPR':
      return 'ValueSejourPR,ColorSejourPR,RecordDateSejourPR';
    case 'IndicatorAutocurage':
      return 'ValueAutocurage,ColorAutocurage,RecordDateAutocurage';
    default:
      return 'ValueTerrain,ColorTerrain,RecordDateTerrain';
  }
};
